/**
 * Pure new-module construction kernel for the sidebar: the position computation
 * and the Module-object shape, testable without React, query client, or the
 * async addModule side effects.
 *
 * The stateful double-submit guard (`isCreatingModuleRef` + `isCreatingModule`)
 * and the await/finally lifecycle live in `useModuleCreation`; this file holds
 * only the deterministic value math.
 */

import { nanoid } from 'nanoid';
import type { Module } from '@/types/modules';
import { MODULE_TYPE_TO_OBJECT } from './constants';

/**
 * Compute the position for a newly inserted module: one less than the smallest
 * existing position (so the new module sorts to the top), with a floor of 0.
 */
export function computeNewModulePosition(modules: ReadonlyArray<{ position?: number }>): number {
  const minPos = modules.reduce(
    (min, m) => (typeof m.position === 'number' && m.position < min ? m.position : min),
    0,
  );
  return minPos - 1;
}

/**
 * Build the new Module object, including the conditional `objectType` spread
 * driven by MODULE_TYPE_TO_OBJECT. `id` is a fresh nanoid (the only
 * non-deterministic field).
 */
export function buildNewModule(args: {
  conversationId: string;
  type: string;
  label: string;
  existingModules: ReadonlyArray<{ position?: number }>;
  nativeOnly?: boolean;
}): Module {
  const { conversationId, type, label, existingModules, nativeOnly } = args;
  const objType = MODULE_TYPE_TO_OBJECT[type];
  return {
    id: nanoid(),
    conversation_id: conversationId,
    type: type as Module['type'],
    label,
    position: computeNewModulePosition(existingModules),
    size: 'normal',
    ...(objType ? { objectType: objType as Module['objectType'] } : {}),
    ...(nativeOnly ? { config: { nativeOnly: true } as Module['config'] } : {}),
  } as Module;
}
